import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { ApiError, getCatalog, getPlaybackAccess, getSession, type Session } from "../services/api";
import { enableSync } from "../services/progress";
import type { Catalog } from "../types";

interface State {
  session: Session | null;
  catalog: Catalog | null;
  error: ApiError | null;
  loading: boolean;
  refresh: () => Promise<void>;
  setSession: (s: Session) => void;
}

const Ctx = createContext<State | null>(null);
const POLL_MS = 3 * 60_000;

export function CatalogProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState(true);
  const syncStarted = useRef(false);

  const refresh = useCallback(async () => {
    try {
      // Acceso temporal a B2 (imágenes y vídeo) antes de pintar el catálogo.
      const [c] = await Promise.all([getCatalog(), getPlaybackAccess()]);
      setCatalog((prev) => (prev && prev.revision === c.revision && prev.generatedAt === c.generatedAt ? prev : c));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e : new ApiError(String(e), 0));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getSession()
      .then(setSession)
      .catch((e) => {
        setError(e instanceof ApiError ? e : new ApiError(String(e), 0));
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!session?.authenticated) {
      if (session) setLoading(false);
      return;
    }
    if (session.progressSync && !syncStarted.current) {
      syncStarted.current = true;
      void enableSync();
    }
    void refresh();
    // Radiant TV detecta las publicaciones del Media Manager sin recompilar: sondeo ligero con ETag.
    const id = setInterval(() => void refresh(), POLL_MS);
    const onVisible = () => document.visibilityState === "visible" && void refresh();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [session, refresh]);

  return <Ctx.Provider value={{ session, catalog, error, loading, refresh, setSession }}>{children}</Ctx.Provider>;
}

export function useCatalog(): State {
  const v = useContext(Ctx);
  if (!v) throw new Error("useCatalog fuera de CatalogProvider");
  return v;
}
